import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { CellProps } from 'react-table'
import {
  CustomTable,
  PlainHeader,
  StackedCell,
  TableColumn,
  TableEmptyState,
} from '../../../../components'
import { AgrupamentoQuantitativo, LinhaQuantitativo } from '../core/_models'
import { getQuantitativoInterconsulta } from '../core/_requests'
import { formatarContagem, formatarHoras, formatarNota } from '../core/filtro'
import { useParametrosRelatorios } from '../core/ListFiltersProvider'

const numero = (
  titulo: string,
  id: keyof LinhaQuantitativo,
  formatar: (v?: number | null) => string,
  cellWidth = '150px'
) =>
  ({
    Header: () => <PlainHeader>{titulo}</PlainHeader>,
    id,
    cellWidth,
    align: 'end',
    Cell: ({ row }: CellProps<LinhaQuantitativo>) => (
      <StackedCell tabular primary={formatar(row.original[id] as number | null)} />
    ),
  } as TableColumn<LinhaQuantitativo>)

const colunas = (
  agrupar: AgrupamentoQuantitativo
): ReadonlyArray<TableColumn<LinhaQuantitativo>> => [
  {
    Header: () => (
      <PlainHeader>{agrupar === 'especialidade' ? 'Especialidade' : 'Solicitante'}</PlainHeader>
    ),
    id: 'chave',
    Cell: ({ row }: CellProps<LinhaQuantitativo>) => (
      <StackedCell primary={row.original.chave || 'Não informado'} />
    ),
  },
  numero('Total', 'total', formatarContagem),
  numero('Respondidas', 'respondidas', formatarContagem),
  numero('Canceladas', 'canceladas', formatarContagem),
  numero('Tempo médio de resposta', 'tempoRespostaMediaHoras', formatarHoras, '220px'),
  {
    Header: () => <PlainHeader>Satisfação média</PlainHeader>,
    id: 'satisfacaoMedia',
    cellWidth: '150px',
    align: 'end',
    Cell: ({ row }: CellProps<LinhaQuantitativo>) => (
      <StackedCell
        tabular
        primary={formatarNota(row.original.satisfacaoMedia)}
        secondary={`${formatarContagem(row.original.satisfacaoN)} avaliações`}
      />
    ),
  },
]

/** Quantitativo do período agrupado por especialidade ou por solicitante (lista completa). */
export const QuantitativoTable = () => {
  const { periodo, agrupar } = useParametrosRelatorios()

  const { data, isFetching, isError, refetch } = useQuery(
    ['interconsulta-relatorio-quantitativo', periodo, agrupar],
    () => getQuantitativoInterconsulta(periodo!, agrupar),
    { enabled: !!periodo, refetchOnWindowFocus: false, retry: false, keepPreviousData: true }
  )

  const columns = useMemo(() => colunas(agrupar), [agrupar])

  return (
    <>
      <CustomTable<LinhaQuantitativo>
        id='kt_table_relatorio_ic_quantitativo'
        ariaLabel={`Quantitativo de interconsultas por ${agrupar}`}
        data={periodo ? data?.linhas ?? [] : []}
        columns={columns}
        isLoading={isFetching}
        isError={isError}
        onRetry={refetch}
        notPaginated
        emptyState={
          periodo ? (
            <TableEmptyState
              title='Nenhuma interconsulta no período'
              description='Revise o período e as unidades escolhidas.'
            />
          ) : (
            <TableEmptyState
              title='Escolha a empresa e o período'
              description='O relatório aparece com a empresa e um período válido (até 366 dias).'
            />
          )
        }
      />
      {periodo && data && data.total > 0 && (
        <p className='text-gray-900 fw-semibold text-end px-9 pb-6 mb-0'>
          Total no período: {formatarContagem(data.total)}
        </p>
      )}
    </>
  )
}
